import React, { useRef, useState } from 'react';
import {
  SPACING,
  RADIUS,
  ACTION_BLUE,
  ACTION_BLUE_HOVER,
} from '../../../theme/theme';
import { useImageLightbox } from '../../Feedback/ImageLightbox';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  MediaItem,
  MediaType,
  useArchiveMedia,
  useMediasList,
  useUploadMedia,
} from '../../../hooks/useMedias';

interface Props {
  publisherId: string;
  applicationId: string;
}

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  PENDING:  { label: 'در انتظار بررسی', bg: '#FEF9C3', color: '#A16207' },
  approved: { label: 'تایید شده',        bg: '#DCFCE7', color: '#15803D' },
  rejected: { label: 'رد شده',           bg: '#FEE2E2', color: '#B91C1C' },
  archived: { label: 'آرشیو شده',        bg: '#F3F4F6', color: '#6B7280' },
};

const MEDIA_SPECS: Record<MediaType, { width: number; height: number }> = {
  banner: { width: 700,  height: 375 },
  post:   { width: 1000, height: 735 },
};

const TABS: { label: string; sub: string; value: MediaType }[] = [
  { label: 'بنر',  sub: '700×375',   value: 'banner' },
  { label: 'پست',  sub: '1000×735',  value: 'post'   },
];

/* ─── Status Badge ─── */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, bg: '#F3F4F6', color: '#374151' };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '10px',
        py: '3px',
        borderRadius: '20px',
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontFamily: 'IRANYekanXFaNum',
        fontSize: '11px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </Box>
  );
};

/* ─── Media Card ─── */
const MediaCard: React.FC<{
  item: MediaItem;
  onArchive: (id: number) => void;
  archiving: boolean;
  previewProps: (src: string, alt?: string) => Record<string, unknown>;
}> = ({ item, onArchive, archiving, previewProps }) => (
  <Box
    sx={{
      borderRadius: '12px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    }}
  >
    {/* Image */}
    <Box
      sx={{
        height: '150px',
        overflow: 'hidden',
        backgroundColor: '#F9FAFB',
      }}
    >
      <img
        src={item.image.url}
        alt="media"
        {...previewProps(item.image.url, 'تصویر اپلیکیشن')}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          cursor: 'zoom-in',
        }}
      />
    </Box>

    {/* Footer */}
    <Box
      sx={{
        p: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        direction: 'rtl',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <StatusBadge status={item.status} />
        <Typography
          sx={{ color: '#9CA3AF', fontFamily: 'IRANYekanXFaNum', fontSize: '11px' }}
        >
          {item.created_at.slice(0, 10)}
        </Typography>
      </Box>

      {item.status !== 'archived' && (
        <Button
          variant="outlined"
          fullWidth
          size="small"
          disabled={archiving}
          onClick={() => onArchive(item.id)}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '12px',
            color: '#374151',
            borderColor: '#D1D5DB',
            borderRadius: '8px',
            py: '5px',
            '&:hover': { borderColor: '#6B7280', backgroundColor: '#F9FAFB' },
          }}
        >
          {archiving ? <CircularProgress size={14} sx={{ color: '#374151' }} /> : 'آرشیو کردن'}
        </Button>
      )}
    </Box>
  </Box>
);

/* ─── Empty State ─── */
const EmptyState: React.FC<{ spec: { width: number; height: number }; onUpload: () => void }> = ({ spec, onUpload }) => (
  <Box
    onClick={onUpload}
    sx={{
      border: '2px dashed #D1D5DB',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 6,
      gap: '8px',
      cursor: 'pointer',
      transition: 'border-color 0.2s, background 0.2s',
      '&:hover': { borderColor: '#1B6FEB', backgroundColor: '#F0F6FF' },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        backgroundColor: '#EFF6FF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        mb: '4px',
      }}
    >
      🖼️
    </Box>
    <Typography sx={{ fontFamily: 'IRANYekanXFaNum', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
      تصویری آپلود نشده
    </Typography>
    <Typography sx={{ fontFamily: 'IRANYekanXFaNum', fontSize: '12px', color: '#9CA3AF' }}>
      ابعاد مجاز: {spec.width}×{spec.height} پیکسل
    </Typography>
    <Typography sx={{ fontFamily: 'IRANYekanXFaNum', fontSize: '12px', color: '#1B6FEB', fontWeight: 500 }}>
      برای آپلود کلیک کنید
    </Typography>
  </Box>
);

/* ─── Error Box ─── */
const ErrorBox: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '8px',
      backgroundColor: '#FEF2F2',
      border: '1px solid #FECACA',
      borderRadius: '10px',
      px: '16px',
      py: '12px',
      mb: '16px',
      direction: 'rtl',
    }}
  >
    <Typography sx={{ fontFamily: 'IRANYekanXFaNum', fontSize: '13px', color: '#B91C1C', flex: 1 }}>
      {message}
    </Typography>
    <Box
      onClick={onClose}
      sx={{ color: '#B91C1C', cursor: 'pointer', fontSize: '16px', lineHeight: 1, mt: '1px' }}
    >
      ✕
    </Box>
  </Box>
);

/* ─── Main Component ─── */
const AppMediaPromotion: React.FC<Props> = ({ publisherId, applicationId }) => {
  const lightbox = useImageLightbox();
  const [activeTab, setActiveTab]     = useState<MediaType>('banner');
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef                  = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError }  = useMediasList(publisherId, applicationId);
  const uploadMutation                = useUploadMedia(publisherId, applicationId);
  const archiveMutation               = useArchiveMedia(publisherId, applicationId);

  const items = (data?.medias ?? []).filter((m) => m.type === activeTab);
  const spec  = MEDIA_SPECS[activeTab];

  const handleUploadClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== spec.width || img.height !== spec.height) {
        setUploadError(
          `ابعاد تصویر باید دقیقاً ${spec.width}×${spec.height} پیکسل باشد — تصویر انتخابی شما ${img.width}×${img.height} است.`,
        );
        return;
      }
      try {
        await uploadMutation.mutateAsync({ file, type: activeTab });
      } catch {
        setUploadError('خطا در آپلود تصویر. دوباره تلاش کنید.');
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setUploadError('فایل انتخابی معتبر نیست.');
    };
    img.src = objectUrl;
  };

  const handleArchive = async (id: number) => {
    setArchivingId(id);
    try {
      await archiveMutation.mutateAsync(id);
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        // Same radius as the cards in the row above.
        borderRadius: `${RADIUS.md}px`,
        overflow: 'hidden',
        padding: `${SPACING.lg}px`,
      }}
    >

      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: '20px',
          direction: 'rtl',
        }}
      >
        {/* Title + Pill Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Typography
            sx={{ fontFamily: 'IRANYekanXFaNum', fontWeight: 700, fontSize: '16px', color: '#111827' }}
          >
            تصاویر پروموشن
          </Typography>

          <Box
            sx={{
              display: 'flex',
              backgroundColor: '#F3F4F6',
              borderRadius: '10px',
              padding: '4px',
              gap: '3px',
            }}
          >
            {TABS.map((t) => {
              const active = activeTab === t.value;
              return (
                <Box
                  key={t.value}
                  onClick={() => { setActiveTab(t.value); setUploadError(null); }}
                  sx={{
                    px: '14px',
                    py: '6px',
                    borderRadius: '7px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    backgroundColor: active ? 'white' : 'transparent',
                    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontSize: '13px',
                      fontWeight: active ? 600 : 400,
                      color: active ? '#1B6FEB' : '#6B7280',
                    }}
                  >
                    {t.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontSize: '11px',
                      color: active ? '#93B5F5' : '#9CA3AF',
                    }}
                  >
                    {t.sub}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Upload Button */}
        <Button
          variant="contained"
          disabled={uploadMutation.isPending}
          onClick={handleUploadClick}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '13px',
            fontWeight: 600,
            color: 'white',
            backgroundColor: ACTION_BLUE,
            '&:hover': {
              backgroundColor: ACTION_BLUE_HOVER,
              boxShadow: '0 4px 12px rgba(44, 62, 219, 0.35)',
            },
            '&.Mui-disabled': { backgroundColor: '#B0BEC5', color: 'white' },
            borderRadius: '8px',
            px: '18px',
            py: '8px',
            boxShadow: '0 2px 6px rgba(27,111,235,0.25)',
            textTransform: 'none',
            minWidth: '130px',
          }}
        >
          {uploadMutation.isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={14} sx={{ color: 'white' }} />
              <span>در حال آپلود...</span>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>آپلود تصویر</span>
              <span style={{ fontSize: '20px', lineHeight: 1, fontWeight: 300 }}>+</span>
            </Box>
          )}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>

      {/* ── Divider ── */}
      <Box sx={{ height: '1px', backgroundColor: '#F3F4F6', mb: '20px' }} />

      {/* ── Error ── */}
      {uploadError && <ErrorBox message={uploadError} onClose={() => setUploadError(null)} />}

      {/* ── Content ── */}
      <Box sx={{ minHeight: '220px' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: '#1B6FEB' }} />
        </Box>
      ) : isError ? (
        <ErrorBox message="خطا در دریافت اطلاعات. دوباره تلاش کنید." onClose={() => {}} />
      ) : items.length === 0 ? (
        <EmptyState spec={spec} onUpload={handleUploadClick} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '16px',
          }}
        >
          {items.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              onArchive={handleArchive}
              archiving={archivingId === item.id}
              previewProps={lightbox.triggerProps}
            />
          ))}
        </Box>
      )}
      </Box>
      {lightbox.element}
    </Box>
  );
};

export default AppMediaPromotion;
